/**
 * Tutor support form section.
 * Programs, optional weekly hours, availability, and engagement dates.
 */
import * as React from 'react';
import { Calendar, CalendarClock, Handshake } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Textarea } from '@/components/forms/Textarea';
import { DatePicker } from '@/components/forms/DatePicker';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { ProgramCascadeFields } from '@/components/common/ProgramCascadeFields';
import { TogglePill } from '@/components/forms/TogglePill';
import {
  ENGAGEMENT_DURATION_OPTIONS,
  TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS,
} from '@/components/tutors/tutor-configs';
import {
  addCalendarMonthsToIso,
  buildEngagementCustomTogglePatch,
  buildEngagementPresetPatch,
  buildEngagementStartDatePatch,
  checkCustomEngagementDurationError,
  computeAllowedSlotsFromPrograms,
  computeDefaultEngagementStartDate,
  normalizeTutorLevelFieldToArray,
} from '@/components/tutors/remote/tutor-form-utils';

/** Remote tutor support section (programs, hours, availability, engagement). */
type Props = Readonly<{
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  programs: any[];
  levelOptions: Array<{ value: string; label: string }>;
  subjectOptions: ReadonlyArray<{ value: string; label: string }>;
  mode: 'create' | 'edit';
}>;

/** Tutor support form section. */
export function TutorSupportSection({
  formData,
  onChange,
  programs,
  levelOptions,
  subjectOptions,
  mode,
}: Props) {
  const isCustom = formData.engagementMode === 'custom';
  const [customEndDateError, setCustomEndDateError] = React.useState<string | undefined>();

  function applySupportPatch(patch: Record<string, unknown>): void {
    Object.entries(patch).forEach(([key, value]) => onChange(key, value));
  }

  const handleDurationClick = (months: number) => {
    setCustomEndDateError(undefined);
    applySupportPatch(buildEngagementPresetPatch(formData, months));
  };

  const handleStartDateChange = (value: string) => {
    if (mode === 'create' && value && value < computeDefaultEngagementStartDate()) return;
    const patch = buildEngagementStartDatePatch(value, isCustom, formData.engagementDurationMonths);
    if (isCustom && formData.engagementEndDate && value) {
      const endError = checkCustomEngagementDurationError(
        value,
        formData.engagementEndDate,
        TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS,
      );
      if (endError) {
        patch.engagementEndDate = '';
        setCustomEndDateError(endError);
      } else {
        setCustomEndDateError(undefined);
      }
    }
    applySupportPatch(patch);
  };

  const handleCustomClick = () => {
    if (isCustom) return;
    setCustomEndDateError(undefined);
    applySupportPatch(buildEngagementCustomTogglePatch(formData));
  };

  const handleCustomEndDateChange = (value: string) => {
    const endError = checkCustomEngagementDurationError(
      formData.engagementStartDate,
      value,
      TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS,
    );
    if (endError) {
      setCustomEndDateError(endError);
      return;
    }
    setCustomEndDateError(undefined);
    onChange('engagementEndDate', value);
  };

  function handleStudentsChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onChange('numberOfStudentsToSupport', Number(e.target.value));
  }

  function handleHoursChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onChange('targetHours', e.target.value);
  }

  function handleVacationsClick(): void {
    onChange('availableOnVacations', formData.availableOnVacations !== true);
  }

  function handleWeekendsClick(): void {
    onChange('availableOnWeekends', formData.availableOnWeekends !== true);
  }

  function handleHolidaysClick(): void {
    onChange('availableOnHolidays', formData.availableOnHolidays !== true);
  }

  const allowedSlots = React.useMemo(
    () => computeAllowedSlotsFromPrograms(programs, formData.programs),
    [formData.programs, programs],
  );

  const levelValues = React.useMemo(
    () => normalizeTutorLevelFieldToArray(formData.level),
    [formData.level],
  );

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Handshake className="w-4 h-4" />
        Disponibilité et Matching
      </h4>

      <ProgramCascadeFields
        programs={programs}
        value={{
          tutoringType: formData.tutoringType,
          coursePath: formData.coursePath,
          programs: formData.programs,
        }}
        onChange={onChange}
        showTutoringType={false}
      />

      <div className="form-section">
        <h4 className="form-section-title">
          <Calendar className="w-4 h-4" />
          Engagement
        </h4>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
            <Input
              label="Nombre d'élèves à soutenir"
              type="number"
              value={formData.numberOfStudentsToSupport ?? ''}
              onChange={handleStudentsChange}
              required
              inputSize="sm"
              placeholder="Ex: 2"
            />
            <Input
              label="Combien d'heures souhaitez-vous effectuer par semaine ?"
              type="number"
              value={formData.targetHours ?? ''}
              onChange={handleHoursChange}
              inputSize="sm"
              placeholder="Ex: 2"
              min={0}
            />
          </div>
          <fieldset className="border-0 p-0 m-0 min-w-0 space-y-2">
            <legend className="form-label form-label-required">Durée de l'engagement</legend>
            <div className="flex gap-2 items-center w-full">
              {ENGAGEMENT_DURATION_OPTIONS.map(({ value, label }) => (
                <TogglePill
                  key={value}
                  label={label}
                  active={!isCustom && formData.engagementDurationMonths === value}
                  onClick={() => handleDurationClick(value)}
                  size="sm"
                  className="h-9 flex-1 min-w-0 inline-flex items-center justify-center text-center"
                />
              ))}
              <TogglePill
                label="Date personnalisée"
                active={isCustom}
                onClick={handleCustomClick}
                size="sm"
                className="h-9 flex-1 min-w-0 inline-flex items-center justify-center text-center"
              />
            </div>
          </fieldset>

          {(formData.engagementDurationMonths || isCustom || formData.engagementStartDate) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DatePicker
                label="Date de début"
                value={formData.engagementStartDate ?? ''}
                onChange={handleStartDateChange}
                required
                inputSize="sm"
                placeholder="JJ/MM/AAAA"
                disablePast={mode === 'create'}
                maxDate={
                  isCustom && formData.engagementEndDate ? formData.engagementEndDate : undefined
                }
              />
              <DatePicker
                label="Date de fin"
                value={formData.engagementEndDate ?? ''}
                onChange={(value) => {
                  if (isCustom) handleCustomEndDateChange(value);
                }}
                required
                inputSize="sm"
                placeholder="JJ/MM/AAAA"
                disabled={!isCustom || !formData.engagementStartDate}
                minDate={
                  isCustom && formData.engagementStartDate
                    ? addCalendarMonthsToIso(
                        formData.engagementStartDate,
                        TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS,
                      )
                    : undefined
                }
                state={customEndDateError ? 'error' : undefined}
                error={customEndDateError}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            <Textarea
              label="Informations équipe"
              value={formData.engagementNotes ?? ''}
              onChange={(e) => onChange('engagementNotes', e.target.value)}
              placeholder="Ex: Équipe pédagogique, club, association partenaire…"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <CalendarClock className="w-4 h-4" />
          Disponibilité
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MultiSelect
              label="Niveaux scolaires des élèves à accompagner"
              placeholder="Sélectionner les niveaux"
              options={levelOptions}
              value={levelValues}
              onChange={(values) => onChange('level', values)}
              required
              maxVisibleTags={3}
              inputSize="sm"
            />
            <Select
              label="Matière enseignée"
              value={formData.subjectToTeach ?? ''}
              onChange={(val) => onChange('subjectToTeach', val)}
              options={subjectOptions}
              placeholder="Sélectionner une matière"
              required
              size="sm"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <fieldset className="border-0 p-0 m-0 min-w-0 space-y-2">
              <legend className="form-label">Disponibilités spécifiques</legend>
              <div className="flex gap-2 items-center w-full">
                <TogglePill
                  label="Vacances scolaires"
                  active={formData.availableOnVacations === true}
                  onClick={handleVacationsClick}
                  size="sm"
                  className="h-9 flex-1 min-w-0 inline-flex items-center justify-center text-center"
                />
                <TogglePill
                  label="Week-end"
                  active={formData.availableOnWeekends === true}
                  onClick={handleWeekendsClick}
                  size="sm"
                  className="h-9 flex-1 min-w-0 inline-flex items-center justify-center text-center"
                />
                <TogglePill
                  label="Jours fériés"
                  active={formData.availableOnHolidays === true}
                  onClick={handleHolidaysClick}
                  size="sm"
                  className="h-9 flex-1 min-w-0 inline-flex items-center justify-center text-center"
                />
              </div>
            </fieldset>
          </div>

          <fieldset className="border-0 p-0 m-0 min-w-0">
            <legend className="text-sm font-medium text-foreground mb-2 block">
              Sélection des créneaux de disponibilité <span className="text-destructive">*</span>
            </legend>
            <AvailabilityGrid
              value={
                formData.availability &&
                typeof formData.availability === 'object' &&
                'weekly' in formData.availability
                  ? formData.availability
                  : { weekly: [] }
              }
              onChange={(value) => onChange('availability', value)}
              allowedSlots={allowedSlots}
            />
          </fieldset>
        </div>
      </div>
    </div>
  );
}
